import React, {
  useRef,
  FC,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import i18n from "@/i18n";
import {
  MapContainer,
  TileLayer,
  Marker as _Marker,
  Popup,
} from "react-leaflet";
import L from "leaflet";
import {
  AppSpecifier,
  FormulaContext,
  LatLongValue,
  MapView,
  RowWithEmbeddedMetadata,
} from "@data-root/types";
import { Preview } from "../../Preview";
import { useDroppable } from "@dnd-kit/core";
import {
  InlineMultipleRowsProps,
  useAppContext,
  useAssets,
  useClientContext,
  useInlineContext,
  withGroups,
  ActionBar,
} from "@data-root/react";

interface MarkerProps {
  appSpecifier: AppSpecifier;
  tableId: string;
  row: RowWithEmbeddedMetadata;
  getContext: () => FormulaContext;

  latlongLabel: string;

  actionIds?: string[];
}

const Marker: FC<MarkerProps> = ({
  appSpecifier,
  tableId,
  row,
  getContext,
  latlongLabel,
  actionIds,
}) => {
  const markerRef = useRef<L.Marker>();

  const { core } = useClientContext();

  const { get } = useAssets();
  const iconUrl = useMemo(() => get("custom-marker"), [get]);
  const markerIcon = useMemo(
    () =>
      new L.Icon({
        iconUrl,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        className: "custom-marker",
      }),
    [iconUrl]
  );

  const value = useMemo<LatLongValue | undefined | null>(
    () => row[latlongLabel] as LatLongValue | undefined | null,
    [row, latlongLabel]
  );

  const position = useMemo(
    () => value && { lat: value.lat, lng: value.long },
    [value]
  );

  const onDrop = useCallback(
    async ({ id, data }: any) => {
      if (data.rowId !== row.id) return;
      markerRef.current.openPopup();
    },

    [row]
  );

  useEffect(() => {
    core.emitter.on("drop", onDrop);

    return () => {
      core.emitter.off("drop", onDrop);
    };
  }, [core, onDrop]);

  if (!position) return null;

  return (
    <_Marker ref={markerRef} key={row.id} icon={markerIcon} position={position}>
      <Popup>
        <Preview
          appSpecifier={appSpecifier}
          tableId={tableId}
          rowId={row.id}
          row={row}
        />

        {actionIds && (
          <ActionBar getContext={getContext} actionIds={actionIds} />
        )}
      </Popup>
    </_Marker>
  );
};

export const Map: FC<InlineMultipleRowsProps> = withGroups(
  ({ getContext, rows, onFetch, loaded }) => {
    const ref = useRef<HTMLDivElement>();
    const observerRef = useRef<ResizeObserver>();
    const mapRef = useRef<L.Map>();
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);

    const { appSpecifier } = useAppContext();
    const { view } = useInlineContext();
    const { options } = view as MapView;

    const { setNodeRef } = useDroppable({
      id: view.id,
      data: {
        type: "view",
        accepts: ["row"],
      },
    });

    const defaultCenter = [40.21755910697642, -74.76485010196036];

    useEffect(() => {
      if (!ref.current) return;
      if (observerRef.current) return;

      setNodeRef(ref.current);

      const observer = new ResizeObserver(() => {
        if (mapRef.current) mapRef.current.invalidateSize();
      });
      observer.observe(ref.current);
      observerRef.current = observer;

      return () => {
        observer.disconnect();
        observerRef.current = undefined;
      };
    }, [setNodeRef]);

    const handleSearch = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!searchQuery.trim() || !mapRef.current) return;
      
      setIsSearching(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`
        );
        const data = await response.json();
        
        if (data && data.length > 0) {
          const { lat, lon } = data[0];
          mapRef.current.setView([parseFloat(lat), parseFloat(lon)], 13);
        }
      } catch (error) {
        console.error("Error searching location:", error);
      } finally {
        setIsSearching(false);
      }
    };

    return (
      <div ref={ref} style={{ width: "100%", height: "100%", position: "relative" }}>
        <div style={{ 
          position: "absolute", 
          top: "10px", 
          left: "50%", 
          transform: "translateX(-50%)", 
          zIndex: 9999,
          width: "80%",
          maxWidth: "400px",
          backgroundColor: "white",
          padding: "5px",
          borderRadius: "4px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.2)"
        }}>
          <form onSubmit={handleSearch} style={{ display: "flex" }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={i18n.t("search_address")}
              style={{ 
                flex: 1, 
                padding: "8px 12px", 
                borderRadius: "4px 0 0 4px",
                border: "1px solid #ccc",
                borderRight: "none",
                outline: "none"
              }}
            />
            <button 
              type="submit" 
              disabled={isSearching}
              style={{ 
                padding: "8px 12px", 
                borderRadius: "0 4px 4px 0",
                border: "1px solid #ccc",
                background: "#fff",
                cursor: isSearching ? "default" : "pointer"
              }}
            >
              {isSearching ? "..." : i18n.t("search")}
            </button>
          </form>
        </div>
        
        <MapContainer
          style={{ width: "100%", height: "100%" }}
          ref={mapRef}
          center={defaultCenter as any}
          zoom={8}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          {rows.map((row) => {
            const getExtendedContext = () => ({
              ...getContext(),
              row: row,
              rowId: row.id,
            });

            return (
              <Marker
                key={row.id}
                getContext={getExtendedContext}
                appSpecifier={appSpecifier}
                tableId={view.tableId}
                row={row}
                latlongLabel={options.latlongLabel}
                actionIds={options.actionIds}
              />
            );
          })}
        </MapContainer>
      </div>
    );
  }
);
